import { FullScreenModal, ICONS_ENUM, Typography } from '@sanlam-glacier/app-common/components';
import { useGetBranding } from '@sanlam-glacier/app-common/modules/theme';
import { useConfig } from 'providers/config/ConfigProvider';
import styles from './exposure-breakdown-takeover.module.scss';
import { Block, BlockProps, Grid } from '@sanlam-glacier/app-common/modules/grid';
import { MultiViewPaper } from 'components/molecules/multi-view-paper/MultiViewPaper';
import { MultiViewPaperViewProps } from 'components/molecules/multi-view-paper/multi-view-paper.type';
import { Reg28InfrastructureExposureTable } from './infrastructure-exposure/Reg28InfrastructureExposureTable';
import { Reg28ActualAssetClassExposureTable } from './asset-class-exposure/Reg28ActualAssetClassExposureTable';
import { Reg28ActualRegionalExposureTable } from './regional-exposure/Reg28ActualRegionalExposureTable';
import { Reg28ActualAssetClassExposureChart } from './asset-class-exposure/Reg28ActualAssetClassExposureChart';
import { Reg28ActualRegionalExposureChart } from './regional-exposure/Reg28ActualRegionalExposureChart';
import { RetirementAnnuityPanel } from './retirement-annuity-panel/RetirementAnnuityPanel';
import clsx from 'clsx';
import {
  Reg28ExposureBreachModelMock,
  Reg28ExposureBreakdownModelMock
} from '../../../types/regulation-28/regulation-28.interfaces';

type Regulation28ExposureModalProps = {
  isOpen: boolean;
  onClose: () => void;
  clientName: string;
  breaches: Reg28ExposureBreachModelMock[];
  exposureBreakdown: Reg28ExposureBreakdownModelMock;
};

export const Regulation28ExposureModal = (props: Regulation28ExposureModalProps) => {
  return props.isOpen ? <Regulation28ExposureModalComponent {...props} /> : null;
};

const Regulation28ExposureModalComponent = ({
  isOpen,
  onClose,
  clientName,
  breaches,
  exposureBreakdown
}: Regulation28ExposureModalProps) => {
  const testComponentName = 'regulation28ExposureBreakdownModal';

  const { theme } = useConfig();
  const { glacierLogoColor, brandLogoColor } = useGetBranding(theme);

  const renderLogo = () => (
    <div className={styles.logoContainer}>
      <img
        data-testid={`${testComponentName}SanlamGlacierLogo`}
        alt='Sanlam Glacier'
        className={styles.topBarIcon}
        src={glacierLogoColor}
      />
      {brandLogoColor && (
        <>
          <div className={styles.logoDivider} />
          <img
            data-testid={`${testComponentName}PartnerLogo`}
            alt='Brand'
            className={styles.brandLogo}
            src={brandLogoColor}
          />
        </>
      )}
    </div>
  );

  const renderHeaderText = () => (
    <div className={styles.headline}>
      <Typography
        data-testid={`${testComponentName}CurrentDateHeading`}
        variant={Typography.VARIANT.H3}
        weight={Typography.WEIGHT.MEDIUM}
      >
        {`Regulation 28 as at ${getCurrentDate()}`}
      </Typography>
      <Typography
        data-testid={`${testComponentName}ClientNameHeading`}
        variant={Typography.VARIANT.H4}
        weight={Typography.WEIGHT.MEDIUM}
      >
        {clientName}
      </Typography>
      <div className={styles.headlineDescription}>
        <Typography
          data-testid={`${testComponentName}HeadingDescription`}
          variant={Typography.VARIANT.B2}
          color={Typography.COLOR.SECONDARY}
        >
          The tables below display the asset class, regional and infrastructure exposures subject to
          Regulation 28 restrictions.
        </Typography>
      </div>
    </div>
  );

  const primaryButton = {
    text: 'Download PDF',
    onClick: () => {}
  };

  return (
    <FullScreenModal
      isOpen={isOpen}
      onClose={onClose}
      logo={renderLogo()}
      primaryButton={primaryButton}
      data-testid={`${testComponentName}FullScreenModal`}
    >
      {renderHeaderText()}
      <RetirementAnnuityPanel breaches={breaches} />

      <Grid gap={3} className={styles.portfolioExposureBreakdownSection}>
        <Grid row gap={3}>
          {renderExposureSection({
            title: 'Actual asset class exposure',
            tooltip: 'Actual asset class exposure',
            tableContent: (
              <Reg28ActualAssetClassExposureTable
                data-testid={`${testComponentName}ActualAssetClassExposureTable`}
                tableData={exposureBreakdown.actualAssetClassExposure ?? []}
                maxLimits={exposureBreakdown.actualAssetClassExposureMaxLimits}
                weights={exposureBreakdown.actualAssetClassExposureWeights}
              />
            ),
            chartContent: (
              <Reg28ActualAssetClassExposureChart
                data-testid={`${testComponentName}ActualAssetClassExposureChart`}
                chartData={exposureBreakdown.actualAssetClassExposureWeights}
                maxLimits={exposureBreakdown.actualAssetClassExposureMaxLimits}
              />
            ),
            blockProps: { xl: 12, lg: 12, md: 12 },
            className: clsx(styles.exposureSectionContainer)
          })}
        </Grid>

        <Grid row gap={3}>
          {renderExposureSection({
            title: 'Actual regional exposure',
            tooltip: 'Actual regional exposure',
            tableContent: (
              <Reg28ActualRegionalExposureTable
                data-testid={`${testComponentName}ActualRegionalExposureTable`}
                tableData={exposureBreakdown.actualRegionalExposure ?? []}
                maxLimits={exposureBreakdown.actualRegionalExposureMaxLimits}
                weights={exposureBreakdown.actualRegionalExposureWeights}
              />
            ),
            chartContent: (
              <Reg28ActualRegionalExposureChart
                data-testid={`${testComponentName}ActualRegionalExposureChart`}
                chartData={exposureBreakdown.actualRegionalExposureWeights}
              />
            ),
            blockProps: { xl: 6, lg: 12, md: 12 },
            className: clsx(
              styles.exposureSectionContainer,
              styles.regionalAndInfrastructureSection
            )
          })}

          {renderExposureSection({
            title: 'Infrastructure exposure',
            tableContent: (
              <Reg28InfrastructureExposureTable
                data-testid={`${testComponentName}InfrastructureExposureTable`}
                tableData={exposureBreakdown.infrastructureExposure ?? []}
                maxLimits={exposureBreakdown.infrastructureExposureMaxLimits}
                weights={exposureBreakdown.infrastructureExposureWeights}
              />
            ),
            blockProps: { xl: 6, lg: 12, md: 12 },
            className: clsx(
              styles.exposureSectionContainer,
              styles.regionalAndInfrastructureSection
            )
          })}
        </Grid>
      </Grid>
    </FullScreenModal>
  );
};

const renderExposureSection = ({
  title,
  tooltip,
  tableContent,
  chartContent,
  blockProps,
  className
}: {
  title: string;
  tooltip?: string;
  tableContent: JSX.Element;
  chartContent?: JSX.Element;
  blockProps: BlockProps;
  className: string;
}) => {
  const views: MultiViewPaperViewProps[] = [
    {
      id: 'table-view',
      icon: ICONS_ENUM.ACTION_VIEW_QUILT,
      content: tableContent
    }
  ];

  if (chartContent) {
    views.push({
      id: 'chart-view',
      icon: ICONS_ENUM.EDITOR_BAR_CHART,
      content: chartContent
    });
  }

  return (
    <Block {...blockProps} className={className}>
      <MultiViewPaper
        data-testid={`ExposureSection${title}`}
        title={title}
        tooltipText={tooltip}
        views={views}
      />
    </Block>
  );
};

const getCurrentDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};
